import { useIntl } from 'react-intl';

import { BUDGET_THRESHOLD_PERCENTAGE } from './constants';

import type { SourcingPurchaseDeactivationReason } from 'api-clients/monolith';

export const useAutomationMessages = () => {
  const intl = useIntl();

  const getAutomationMessages = (
    reason:
      | SourcingPurchaseDeactivationReason
      | null
      | 'target_budget_breached', // This is a custom reason for the banner to show when the budget is breached. Other reasons are from the backend.
    date?: Date | null,
    spendPercentage?: number,
  ) => {
    const formattedDate = date
      ? intl.formatDate(date, {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        })
      : null;

    switch (reason) {
      case 'automated_as_goal_met': {
        return {
          activeTitle: formattedDate
            ? intl.formatMessage(
                {
                  defaultMessage: 'Sourcing Resumed – {date}',
                },
                { date: formattedDate },
              )
            : intl.formatMessage({
                defaultMessage: 'Sourcing Resumed',
              }),
          pausedTitle: formattedDate
            ? intl.formatMessage(
                {
                  defaultMessage: 'Sourcing Paused – {date}',
                },
                { date: formattedDate },
              )
            : intl.formatMessage({
                defaultMessage: 'Sourcing Paused',
              }),
          activeDescription: intl.formatMessage({
            defaultMessage:
              'Sourcing has been resumed as we have insufficient applicants in the funnel.',
          }),
          pausedDescription: intl.formatMessage({
            defaultMessage:
              'Sourcing has been paused automatically as we have sufficient applicants in the funnel.',
          }),
        };
      }

      case 'hiring_goal_ended': {
        return {
          activeTitle: intl.formatMessage({
            defaultMessage: 'Sourcing Active – New Hiring Goal Added',
          }),
          pausedTitle: intl.formatMessage({
            defaultMessage: 'Sourcing Paused – Hiring Goal Ended',
          }),
          activeDescription: intl.formatMessage({
            defaultMessage:
              'A new hiring goal has started, and some campaigns were affected.',
          }),
          pausedDescription: intl.formatMessage({
            defaultMessage:
              'The hiring goal for this opening has ended. All campaigns have been paused. Set a new hiring goal to continue sourcing.',
          }),
        };
      }

      case 'target_budget_met': {
        return {
          activeTitle: intl.formatMessage({
            defaultMessage: 'Sourcing Active – Budget Updated',
          }),
          pausedTitle: intl.formatMessage({
            defaultMessage: 'Sourcing Paused – Target budget reached',
          }),
          activeDescription: intl.formatMessage({
            defaultMessage:
              "Your hiring budget has increased. We've automatically resumed campaigns paused by budget automation.",
          }),
          pausedDescription: intl.formatMessage({
            defaultMessage:
              "You've used 100% of your target budget. Campaigns are paused automatically to prevent overspending. Update the budget to resume sourcing.",
          }),
        };
      }

      case 'target_budget_breached': {
        return {
          activeTitle: intl.formatMessage({
            defaultMessage: 'Approaching Budget Limit',
          }),
          pausedTitle: '',
          activeDescription: intl.formatMessage(
            {
              defaultMessage:
                "You've used {spendPercentage}% of your target budget. Campaigns will auto-pause when the limit is reached. Update budget to continue hiring.",
            },
            {
              spendPercentage:
                spendPercentage ?? BUDGET_THRESHOLD_PERCENTAGE * 100,
            },
          ),
          pausedDescription: '',
        };
      }

      default: {
        return null;
      }
    }
  };

  return { getAutomationMessages };
};
